import {
    ApplicationConfig,
    ErrorHandler,
    inject,
    PLATFORM_ID,
    provideAppInitializer,
    provideBrowserGlobalErrorListeners,
    provideZonelessChangeDetection,
} from '@angular/core';
import { isPlatformServer } from '@angular/common';
import { provideRouter, Router } from '@angular/router';
import { provideToastr } from 'ngx-toastr';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideHttpClient, withInterceptors, withInterceptorsFromDi, withFetch } from '@angular/common/http';
import { provideServiceWorker } from '@angular/service-worker';
import * as Sentry from '@sentry/angular';
import { firstValueFrom } from 'rxjs';
import { TraceService } from '@sentry/angular';

import { routes } from './app.routes';
import { httpRequestInterceptor } from './core/interceptors/http-request.interceptor';
import { connectivityInterceptor } from './core/interceptors/retry-on-reconnect.interceptor';
import {
    API_KEY,
    BASE_URL,
    IS_PRODUCTION,
    RECAPTCHA_API_URL,
    RECAPTCHA_HEADER,
    RECAPTCHA_SITE_KEY,
} from './core/global/constants';
import { AppInitService } from './core/services/app-init.service';

function provideSentryProviders(platformId: object): unknown[] {
    if (isPlatformServer(platformId)) {
        return [];
    }
    return [
        {
            provide: ErrorHandler,
            useValue: Sentry.createErrorHandler({
                logErrors: true,
                showDialog: true,
            }),
        },
        {
            provide: Sentry.TraceService,
            useClass: Sentry.TraceService,
            deps: [Router],
        },
    ];
}

function initSentry(appInitService: AppInitService) {
    try {
        const sentryDSN = appInitService.getConfigValueByKey('sentryDSN');
        const baseUrl = appInitService.getConfigValueByKey('baseUrl');

        if (!sentryDSN) {
            console.warn('Sentry DSN not configured, skipping Sentry initialization');
            return;
        }

        const integrations = [Sentry.browserTracingIntegration()];
        if (IS_PRODUCTION) {
            integrations.push(
                Sentry.replayIntegration({ maskAllText: false, blockAllMedia: false })
            );
        }

        Sentry.init({
            environment: IS_PRODUCTION ? 'production' : 'development',
            dsn: sentryDSN,
            integrations,
            tracesSampleRate: 0.1,
            tracePropagationTargets: ['localhost', baseUrl],
            replaysSessionSampleRate: IS_PRODUCTION ? 0.1 : 0.0,
            replaysOnErrorSampleRate: IS_PRODUCTION ? 1.0 : 0.0,
        });
    } catch (error) {
        console.warn('Sentry initialization failed:', error);
    }
}

async function initializeApp(): Promise<void> {
    try {
        const appInitService = inject(AppInitService);
        await appInitService.loadConfig();
        initSentry(appInitService);
    } catch (error) {
        console.error('App initialization failed:', error);
    }
}

const providers = [
    {
        provide: BASE_URL,
        useFactory: async (appInitService: AppInitService) => {
            await firstValueFrom(appInitService.configLoadedSubject);
            return appInitService.getConfigValueByKey('baseUrl') + '/genesis/';
        },
        deps: [AppInitService],
    },
    {
        provide: API_KEY,
        useFactory: async (appInitService: AppInitService) => {
            await firstValueFrom(appInitService.configLoadedSubject);
            return appInitService.getConfigValueByKey('apiKey');
        },
        deps: [AppInitService],
    },
    {
        provide: RECAPTCHA_SITE_KEY,
        useFactory: async (appInitService: AppInitService) => {
            await firstValueFrom(appInitService.configLoadedSubject);
            return appInitService.getConfigValueByKey('recaptchaSiteKey');
        },
        deps: [AppInitService],
    },
    {
        provide: RECAPTCHA_HEADER,
        useFactory: async (appInitService: AppInitService) => {
            await firstValueFrom(appInitService.configLoadedSubject);
            return appInitService.getConfigValueByKey('recaptchaHeader');
        },
        deps: [AppInitService],
    },
    {
        provide: RECAPTCHA_API_URL,
        useFactory: async (appInitService: AppInitService) => {
            await firstValueFrom(appInitService.configLoadedSubject);
            return appInitService.getConfigValueByKey('recaptchaApiUrl');
        },
        deps: [AppInitService],
    },
    {
        provide: 'sentryProviders',
        useFactory: provideSentryProviders,
        deps: [PLATFORM_ID],
    },
];

export const appConfig: ApplicationConfig = {
    providers: [
        provideAppInitializer(async () => {
            inject(TraceService);
            return initializeApp();
        }),
        provideBrowserGlobalErrorListeners(),
        provideZonelessChangeDetection(),
        provideRouter(routes),
        provideAnimations(),
        provideHttpClient(
            withFetch(),
            withInterceptorsFromDi(),
            withInterceptors([connectivityInterceptor, httpRequestInterceptor]),
        ),
        provideToastr(),
        provideServiceWorker('ngsw-worker.js', {
            enabled: true,
            registrationStrategy: 'registerWhenStable:30000'
        }),
        ...providers,
    ],
};
